/**
 * Intake form (spec page 2a) - Microsoft sign-in, mobile-first for field
 * workers. Plain-words type, urgency hint, optional link. Lands in New;
 * Baker scores it at review. Plus the requester's own "My requests" list.
 */
import { useState } from 'react';
import { TYPES, URGENCY_HINTS, type DivisionId, type RequestType, type UrgencyHint } from '../config';
import { ageLabel, submitRequest, type Me, type Req } from '../lib/data';
import { DivisionPicker, Empty, StatusTag } from '../ui';

export function SubmitForm({ me }: { me: Me }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<RequestType | ''>('');
  const [urgencyHint, setUrgencyHint] = useState<UrgencyHint>('week');
  const [divisions, setDivisions] = useState<DivisionId[]>(me.division ? [me.division] : []);
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const ready = title.trim() && type && divisions.length;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!ready) return;
    setBusy(true); setMsg(null);
    try {
      await submitRequest(me, { title, description, type: type as RequestType, divisions, urgencyHint, link });
      setTitle(''); setDescription(''); setType(''); setLink('');
      setMsg({ ok: true, text: "Got it - Baker will review your request. You can track it below." });
    } catch (err) {
      setMsg({ ok: false, text: (err as Error).message });
    } finally { setBusy(false); }
  }

  return (
    <form className="card form" onSubmit={submit}>
      <h2>New request</h2>
      <p className="muted">Signed in as <b>{me.name}</b></p>

      <label>What do you need?
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} placeholder="e.g. Add new hires to HubSpot" required />
      </label>

      <fieldset>
        <legend>What kind of request?</legend>
        <div className="radio-grid">
          {TYPES.map((t) => (
            <label key={t.id} className={`radio ${type === t.id ? 'on' : ''}`}>
              <input type="radio" name="type" checked={type === t.id} onChange={() => setType(t.id)} />
              {t.plain}
            </label>
          ))}
        </div>
      </fieldset>

      <label>Details
        <textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={20000}
          placeholder="What's happening, which job/record, anything that helps" />
      </label>

      <fieldset>
        <legend>How urgent?</legend>
        <div className="radio-row">
          {URGENCY_HINTS.map((u) => (
            <label key={u.id} className={`radio ${urgencyHint === u.id ? 'on' : ''}`}>
              <input type="radio" name="urg" checked={urgencyHint === u.id} onChange={() => setUrgencyHint(u.id)} />
              {u.label}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset>
        <legend>Which division is this for?</legend>
        <DivisionPicker value={divisions} onChange={setDivisions} />
      </fieldset>

      <label>Screenshot or link (optional)
        <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Paste a link to a screenshot, record or file" />
      </label>

      <button type="submit" className="big" disabled={!ready || busy}>{busy ? 'Sending...' : 'Submit request'}</button>
      {msg && <p className={msg.ok ? 'ok' : 'err'}>{msg.text}</p>}
    </form>
  );
}

export function MyRequests({ items }: { items: Req[] }) {
  const mine = [...items].sort((a, b) => (b.createdAt?.toMillis() ?? 0) - (a.createdAt?.toMillis() ?? 0));
  return (
    <div className="card">
      <h2>My requests</h2>
      {!mine.length && <Empty>Nothing submitted yet.</Empty>}
      {mine.map((r) => (
        <div key={r.id} className="row">
          <div className="row-main">
            <div className="row-title">{r.title}</div>
            <div className="row-meta">Submitted {ageLabel(r.createdAt)} ago</div>
          </div>
          <StatusTag status={r.status} />
        </div>
      ))}
    </div>
  );
}
